import React, { useEffect, useState } from "react";
import { orderApi } from "../services/api";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from "@mui/material";

const getStatusColor = (status) => {
  switch (status) {
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "cancelled":
      return "error";
    default:
      return "default";
  }
};

const money = (value) => Number(value || 0).toFixed(2);

const getOrderBreakdown = (order) => {
  const subtotal = Number(order?.subtotalAmount ?? 0) ||
    (order?.items || []).reduce((sum, item) => {
      return sum + Number(item?.price || 0) * Number(item?.quantity || 0);
    }, 0);
  const delivery = Number(order?.deliveryCharge || 0);
  const wallet = Number(order?.walletAmount || 0);
  const payable = Number(order?.amountToPayByCard ?? order?.totalAmount ?? 0);

  return { subtotal, delivery, wallet, payable };
};

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await orderApi.getUserOrders();
      const data = res.data?.orders || res.data || [];
      setOrders(data);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (orders.length === 0) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography>No orders found</Typography>

        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate("/")}
        >
          Start Shopping
        </Button>
      </Box>
    );
  }

 return (
  <Box
    sx={{
      minHeight: "100vh",
      background:
        "linear-gradient(135deg,#fff8f6 0%, #f7ece7 100%)",
      py: 5,
      px: 2
    }}
  >

    <Box
      maxWidth="1100px"
      mx="auto"
    >

      {/* HEADER */}
      <Box
        mb={5}
        display="flex"
        justifyContent="space-between"
        alignItems={{
          xs: "flex-start",
          md: "center"
        }}
        flexDirection={{
          xs: "column",
          md: "row"
        }}
        gap={2}
      >

        <Box>

          <Typography
            variant="h3"
            fontWeight={700}
            sx={{
              color: "#5c0000"
            }}
          >
            My Orders
          </Typography>

          <Typography
            sx={{
              color: "#7b7b7b",
              mt: 1
            }}
          >
            Track and manage all your
            purchased products
          </Typography>

        </Box>

        <Box display="flex" gap={2}>

          <Button
            variant="outlined"
            onClick={() => navigate("/")}
            sx={{
              borderRadius: "14px",
              borderColor: "#8B0000",
              color: "#8B0000",
              fontWeight: 700,
              px: 3,
              py: 1.2,
              textTransform: "none"
            }}
          >
            Home
          </Button>

          <Button
            variant="contained"
            onClick={() => navigate("/cart")}
            sx={{
              borderRadius: "14px",
              px: 3,
              py: 1.2,
              fontWeight: 700,
              textTransform: "none",
              background:
                "linear-gradient(135deg,#5c0000,#8B0000)",
              boxShadow:
                "0 10px 25px rgba(139,0,0,0.2)"
            }}
          >
            Go to Cart
          </Button>

        </Box>

      </Box>

      {/* EMPTY STATE */}
      {orders.length === 0 ? (

        <Card
          sx={{
            borderRadius: "28px",
            p: 5,
            textAlign: "center",
            boxShadow:
              "0 15px 40px rgba(0,0,0,0.06)"
          }}
        >

          <Typography
            variant="h5"
            fontWeight={700}
            mb={2}
          >
            No Orders Found
          </Typography>

          <Typography
            color="text.secondary"
            mb={3}
          >
            Looks like you haven't placed
            any orders yet.
          </Typography>

          <Button
            variant="contained"
            onClick={() => navigate("/")}
            sx={{
              borderRadius: "14px",
              px: 4,
              py: 1.3,
              fontWeight: 700,
              textTransform: "none",
              background:
                "linear-gradient(135deg,#5c0000,#8B0000)"
            }}
          >
            Start Shopping
          </Button>

        </Card>

      ) : (

        orders.map((order) => (

          <Card
            key={order._id}
            sx={{
              mb: 4,
              borderRadius: "26px",
              overflow: "hidden",
              boxShadow:
                "0 12px 35px rgba(139,0,0,0.08)",
              border:
                "1px solid rgba(139,0,0,0.08)",
              transition: "0.3s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow:
                  "0 18px 40px rgba(139,0,0,0.14)"
              }
            }}
          >

            {/* TOP BAR */}
            <Box
              sx={{
                background:
                  "linear-gradient(135deg,#5c0000,#8B0000)",
                color: "#fff",
                px: 4,
                py: 2.5
              }}
            >

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{
                  xs: "flex-start",
                  md: "center"
                }}
                flexDirection={{
                  xs: "column",
                  md: "row"
                }}
                gap={2}
              >

                <Box>

                  <Typography
                    fontWeight={700}
                    fontSize={17}
                  >
                    Order ID
                  </Typography>

                  <Typography
                    sx={{
                      opacity: 0.9,
                      mt: 0.5
                    }}
                  >
                    {order._id}
                  </Typography>

                </Box>

                <Box
                  textAlign={{
                    xs: "left",
                    md: "right"
                  }}
                >

                  <Typography
                    sx={{
                      opacity: 0.85
                    }}
                  >
                    {new Date(
                      order.createdAt
                    ).toLocaleString()}
                  </Typography>

                  <Chip
                    label={order.status}
                    sx={{
                      mt: 1,
                      fontWeight: 700,
                      textTransform:
                        "capitalize",
                      background:
                        order.status ===
                        "completed"
                          ? "#dcfce7"
                          : order.status ===
                            "pending"
                          ? "#fff3cd"
                          : "#fde8e8",
                      color:
                        order.status ===
                        "completed"
                          ? "#166534"
                          : order.status ===
                            "pending"
                          ? "#92400e"
                          : "#b91c1c"
                    }}
                  />

                </Box>

              </Box>

            </Box>

            {/* CONTENT */}
            <CardContent sx={{ p: 4 }}>

              {/* ITEMS */}
              <Box
                display="flex"
                flexWrap="wrap"
                gap={3}
              >

                {order.items
                  ?.slice(0, 3)
                  .map((item, idx) => (

                    <Box
                      key={idx}
                      display="flex"
                      alignItems="center"
                      gap={2}
                      sx={{
                        background:
                          "#fff8f6",
                        border:
                          "1px solid rgba(139,0,0,0.08)",
                        borderRadius:
                          "18px",
                        p: 2,
                        minWidth: 250
                      }}
                    >

                      <img
                        src={item.image}
                        alt={item.name}
                        style={{
                          width: 70,
                          height: 70,
                          borderRadius: 14,
                          objectFit:
                            "cover"
                        }}
                      />

                      <Box>

                        <Typography
                          fontWeight={700}
                        >
                          {item.name}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mt={0.5}
                        >
                          Quantity:
                          {" "}
                          {item.quantity}
                        </Typography>

                      </Box>

                    </Box>

                  ))}

              </Box>

              {/* FOOTER */}
              <Box
                mt={4}
                display="flex"
                justifyContent="space-between"
                alignItems={{
                  xs: "flex-start",
                  md: "center"
                }}
                flexDirection={{
                  xs: "column",
                  md: "row"
                }}
                gap={2}
              >

                <Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Amount to Pay
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{
                      color: "#5c0000"
                    }}
                  >
                    Rs.{money(getOrderBreakdown(order).payable)}
                  </Typography>

                  {/* Wallet Breakdown */}
                  {getOrderBreakdown(order).wallet > 0 && (
                    <Box mt={2} sx={{ fontSize: 13 }}>
                      <Typography variant="caption" color="text.secondary">
                        Subtotal: Rs.{money(getOrderBreakdown(order).subtotal)}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        Wallet Used: Rs.{money(getOrderBreakdown(order).wallet)}
                      </Typography>
                    </Box>
                  )}

                </Box>

                <Box display="flex" gap={2}>

                  <Button
                    variant="outlined"
                    onClick={() =>
                      setSelectedOrder(order)
                    }
                    sx={{
                      borderRadius:
                        "14px",
                      borderColor:
                        "#8B0000",
                      color: "#8B0000",
                      fontWeight: 700,
                      px: 3,
                      textTransform:
                        "none"
                    }}
                  >
                    Quick View
                  </Button>

                  <Button
                    variant="contained"
                    onClick={() =>
                      navigate(
                        `/order-confirmation/${order._id}`
                      )
                    }
                    sx={{
                      borderRadius:
                        "14px",
                      px: 3,
                      fontWeight: 700,
                      textTransform:
                        "none",
                      background:
                        "linear-gradient(135deg,#5c0000,#8B0000)"
                    }}
                  >
                    Full Details
                  </Button>

                </Box>

              </Box>

            </CardContent>

          </Card>

        ))

      )}

      {/* MODAL */}
      <Dialog
        open={Boolean(selectedOrder)}
        onClose={() =>
          setSelectedOrder(null)
        }
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "28px",
            overflow: "hidden"
          }
        }}
      >

        <Box
          sx={{
            background:
              "linear-gradient(135deg,#5c0000,#8B0000)",
            color: "#fff",
            px: 4,
            py: 3
          }}
        >

          <Typography
            variant="h5"
            fontWeight={700}
          >
            Order Details
          </Typography>

        </Box>

        <DialogContent sx={{ p: 4 }}>

          {selectedOrder?.items?.map(
            (item, idx) => (

              <Box
                key={idx}
                display="flex"
                gap={2}
                mb={3}
                sx={{
                  background:
                    "#fff8f6",
                  p: 2,
                  borderRadius:
                    "18px"
                }}
              >

                <img
                  src={item.image}
                  alt={item.name}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 14,
                    objectFit:
                      "cover"
                  }}
                />

                <Box>

                  <Typography
                    fontWeight={700}
                    fontSize={16}
                  >
                    {item.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    mt={1}
                  >
                    Quantity:
                    {" "}
                    {item.quantity}
                  </Typography>

                  <Typography
                    variant="body2"
                    mt={0.5}
                  >
                    Price:
                    {" "}
                    ₹{item.price}
                  </Typography>

                </Box>

              </Box>

            )
          )}

          <Divider sx={{ my: 3 }} />

          <Box
            display="flex"
            justifyContent="space-between"
          >

            <Typography
              fontWeight={700}
            >
              Amount to Pay
            </Typography>

            <Typography
              fontWeight={700}
              sx={{
                color: "#8B0000"
              }}
            >
              Rs.{money(getOrderBreakdown(selectedOrder).payable)}
            </Typography>

          </Box>

          {/* Payment Breakdown */}
          {(
            <>
              <Divider sx={{ my: 3 }} />
              
              <Typography fontWeight={700} mb={2}>
                Payment Breakdown
              </Typography>

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2" fontWeight={700}>
                  Rs.{money(getOrderBreakdown(selectedOrder).subtotal)}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Delivery</Typography>
                <Typography variant="body2" fontWeight={700}>
                  {getOrderBreakdown(selectedOrder).delivery === 0
                    ? "Free"
                    : `Rs.${money(getOrderBreakdown(selectedOrder).delivery)}`}
                </Typography>
              </Box>

              {getOrderBreakdown(selectedOrder).wallet > 0 && (
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Wallet Used</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: "#8B0000" }}>
                  - Rs.{money(getOrderBreakdown(selectedOrder).wallet)}
                </Typography>
              </Box>
              )}

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Amount to Pay on Delivery</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: "#8B0000" }}>
                  Rs.{money(getOrderBreakdown(selectedOrder).payable)}
                </Typography>
              </Box>
            </>
          )}

        </DialogContent>

        <DialogActions
          sx={{
            px: 4,
            pb: 4
          }}
        >

          <Button
            onClick={() =>
              setSelectedOrder(null)
            }
            sx={{
              color: "#8B0000",
              fontWeight: 700
            }}
          >
            Close
          </Button>

          <Button
            variant="contained"
            onClick={() =>
              navigate(
                `/order-confirmation/${selectedOrder._id}`
              )
            }
            sx={{
              borderRadius: "12px",
              px: 3,
              fontWeight: 700,
              textTransform: "none",
              background:
                "linear-gradient(135deg,#5c0000,#8B0000)"
            }}
          >
            View Full Page
          </Button>

        </DialogActions>

      </Dialog>

    </Box>

  </Box>
);
};

export default MyOrders;
